import { useEffect, useMemo, useState } from 'react'
import { Box, FileStack, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { type LexProduct } from '@/shared/hooks/api/product'
import { useListProductSources } from '@/shared/hooks/api/productSource'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { PopoverCombobox } from '@/components/ui/popover-combobox'
import {
  type ProductChatUnavailableReason,
  getProductChatAvailability,
} from '@/features/product-catalog/utils/productChatAvailability'
import type { NewChatMode } from '../hooks/useLlmChatDraftState'

type LlmChatNewChatWizardProps = {
  draftMode: NewChatMode | null
  productsLoading: boolean
  products: LexProduct[]
  selectedDraftProduct: LexProduct | null
  onChooseGeneral: () => void
  onChooseProduct: () => void
  onBack: () => void
  onOpenCatalog: () => void
  onContinueProduct: (product: LexProduct) => void
}

function getProductLabel(product: LexProduct, noNameFallback: string): string {
  const name = product.name?.trim()

  if (!name) {
    return noNameFallback
  }

  return name
}

function getProductChatUnavailableMessageKey(
  reason: ProductChatUnavailableReason
): string {
  if (reason === 'failed') {
    return 'newChatWizard.productSelect.disabledFailed'
  }
  if (reason === 'processing') {
    return 'newChatWizard.productSelect.disabledProcessing'
  }
  return 'newChatWizard.productSelect.disabledUnavailable'
}

function getProductChatUnavailableLinkKey(
  reason: ProductChatUnavailableReason
): string {
  if (reason === 'unavailable') {
    return 'newChatWizard.productSelect.goToProductPageAdd'
  }
  return 'newChatWizard.productSelect.goToProductPageStatus'
}

export function LlmChatNewChatWizard({
  draftMode,
  productsLoading,
  products,
  selectedDraftProduct,
  onChooseGeneral,
  onChooseProduct,
  onBack,
  onOpenCatalog,
  onContinueProduct,
}: LlmChatNewChatWizardProps) {
  const { t } = useTranslation('llmChat')
  const [pickerProduct, setPickerProduct] = useState<LexProduct | null>(null)
  const noNameProductLabel = t('newChatWizard.productSelect.noName')
  const pickerProductSourcesQuery = useListProductSources(pickerProduct?.id)

  const isChoosingChatMode = draftMode == null
  const isSelectingProduct =
    draftMode === 'product' && selectedDraftProduct == null
  const isPromptingFirstMessage = !isChoosingChatMode && !isSelectingProduct

  const isProductsLoading = productsLoading
  const isProductListEmpty = products.length === 0
  const showProductsLoading = isSelectingProduct && isProductsLoading
  const showEmptyProductList =
    isSelectingProduct && !isProductsLoading && isProductListEmpty
  const showProductPicker =
    isSelectingProduct && !isProductsLoading && !isProductListEmpty

  const hasPickerProduct = pickerProduct != null
  const hasPickerProductSources = pickerProductSourcesQuery.data != null
  const isCheckingPickerProductSources =
    hasPickerProduct && pickerProductSourcesQuery.isLoading
  const hasPickerProductSourcesError = pickerProductSourcesQuery.isError

  const pickerProductChatAvailability = useMemo(() => {
    if (!hasPickerProduct || !hasPickerProductSources) {
      return null
    }
    return getProductChatAvailability({
      product: pickerProduct,
      sources: pickerProductSourcesQuery.data,
    })
  }, [hasPickerProduct, hasPickerProductSources, pickerProduct, pickerProductSourcesQuery.data])

  const pickerProductUnavailableReason =
    pickerProductChatAvailability?.chatUnavailableReason ?? null
  const isPickerProductUnavailable = pickerProductUnavailableReason != null
  const canStartPickerProductChat =
    pickerProductChatAvailability?.canStartProductChat === true

  const pickerProductCanContinue =
    hasPickerProduct &&
    !isCheckingPickerProductSources &&
    !hasPickerProductSourcesError &&
    canStartPickerProductChat

  const pickerProductStatusMessage = useMemo(() => {
    if (isCheckingPickerProductSources) {
      return t('newChatWizard.productSelect.checkingSources')
    }
    if (hasPickerProductSourcesError) {
      return t('newChatWizard.productSelect.checkError')
    }
    if (isPickerProductUnavailable) {
      return t(
        getProductChatUnavailableMessageKey(pickerProductUnavailableReason)
      )
    }
    return null
  }, [
    hasPickerProductSourcesError,
    isCheckingPickerProductSources,
    isPickerProductUnavailable,
    pickerProductUnavailableReason,
    t,
  ])

  const pickerProductStatusLinkKey = useMemo(() => {
    if (!isPickerProductUnavailable) {
      return null
    }
    return getProductChatUnavailableLinkKey(pickerProductUnavailableReason)
  }, [isPickerProductUnavailable, pickerProductUnavailableReason])

  const showPickerProductStatus = pickerProductStatusMessage != null
  const showPickerProductStatusLink =
    hasPickerProduct && pickerProductStatusLinkKey != null

  const shouldClearPickerProduct = draftMode !== 'product'

  useEffect(() => {
    if (shouldClearPickerProduct) {
      setPickerProduct(null)
    }
  }, [shouldClearPickerProduct])

  const handleContinueProduct = () => {
    if (!pickerProductCanContinue || pickerProduct == null) {
      return
    }
    onContinueProduct(pickerProduct)
  }

  if (isChoosingChatMode) {
    return (
      <div className='mx-auto flex w-full max-w-3xl flex-col px-4 py-3 md:px-5'>
        <div className='mx-auto flex w-full max-w-2xl flex-col gap-4 py-10'>
          <div className='space-y-1 text-center'>
            <h2 className='text-lg font-semibold'>
              {t('newChatWizard.title')}
            </h2>
            <p className='text-sm text-muted-foreground'>
              {t('newChatWizard.subtitle')}
            </p>
          </div>
          <div className='grid gap-4 sm:grid-cols-2'>
            <Card className='cursor-pointer transition-colors hover:bg-accent/40'>
              <button
                type='button'
                className='h-full w-full text-left'
                onClick={onChooseGeneral}
              >
                <CardHeader>
                  <FileStack
                    className='mb-2 size-9 text-muted-foreground'
                    aria-hidden
                  />
                  <CardTitle className='text-base'>
                    {t('newChatWizard.general.title')}
                  </CardTitle>
                  <CardDescription>
                    {t('newChatWizard.general.description')}
                  </CardDescription>
                </CardHeader>
              </button>
            </Card>
            <Card className='cursor-pointer transition-colors hover:bg-accent/40'>
              <button
                type='button'
                className='h-full w-full text-left'
                onClick={onChooseProduct}
              >
                <CardHeader>
                  <Box
                    className='mb-2 size-9 text-muted-foreground'
                    aria-hidden
                  />
                  <CardTitle className='text-base'>
                    {t('newChatWizard.product.title')}
                  </CardTitle>
                  <CardDescription>
                    {t('newChatWizard.product.description')}
                  </CardDescription>
                </CardHeader>
              </button>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  if (isSelectingProduct) {
    return (
      <div className='mx-auto flex w-full max-w-3xl flex-col px-4 py-3 md:px-5'>
        <div className='mx-auto flex w-full max-w-xl flex-col gap-4 py-10'>
          <div className='space-y-1 text-center'>
            <h2 className='text-lg font-semibold'>
              {t('newChatWizard.productSelect.title')}
            </h2>
            <p className='text-sm text-muted-foreground'>
              {t('newChatWizard.productSelect.subtitle')}
            </p>
          </div>
          {showProductsLoading ? (
            <div className='flex items-center justify-center py-8'>
              <Loader2 className='size-6 animate-spin text-muted-foreground' />
            </div>
          ) : null}
          {showEmptyProductList ? (
            <div className='flex flex-col items-center gap-3 rounded-md border border-dashed p-6 text-center'>
              <p className='text-sm text-muted-foreground'>
                {t('newChatWizard.productSelect.empty')}
              </p>
              <Button type='button' variant='secondary' onClick={onOpenCatalog}>
                {t('newChatWizard.productSelect.openCatalog')}
              </Button>
            </div>
          ) : null}
          {showProductPicker ? (
            <div className='space-y-3'>
              <PopoverCombobox
                items={products}
                value={pickerProduct}
                onValueChange={setPickerProduct}
                getItemLabel={(product) =>
                  getProductLabel(product, noNameProductLabel)
                }
                getItemKey={(product) => product.id}
                placeholder={t('newChatWizard.productSelect.placeholder')}
                searchPlaceholder={t(
                  'newChatWizard.productSelect.searchPlaceholder'
                )}
                emptyText={t('newChatWizard.productSelect.noMatch')}
                showClear
              />
              {showPickerProductStatus ? (
                <p className='text-sm text-muted-foreground'>
                  {pickerProductStatusMessage}{' '}
                  {showPickerProductStatusLink ? (
                    <Link
                      to={`/product-catalog/${pickerProduct.id}`}
                      className='font-medium text-primary underline-offset-4 hover:underline'
                    >
                      {t(pickerProductStatusLinkKey)}
                    </Link>
                  ) : null}
                </p>
              ) : null}
              <div className='flex justify-between gap-2'>
                <Button type='button' variant='outline' onClick={onBack}>
                  {t('newChatWizard.back')}
                </Button>
                <Button
                  type='button'
                  disabled={!pickerProductCanContinue}
                  onClick={handleContinueProduct}
                >
                  {t('newChatWizard.continue')}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  if (isPromptingFirstMessage) {
    return (
      <div className='mx-auto flex w-full max-w-3xl flex-col px-4 py-3 md:px-5'>
        <p className='py-12 text-center text-[13px] text-muted-foreground'>
          {t('promptFirstMessage')}
        </p>
      </div>
    )
  }

  return null
}
